import { useCallback, useRef } from 'react';

/**
 * Incident scrub/add preview handlers for the policy page. They stay memoized on the store calls,
 * so they read handleError and setLoadingState through a ref: both are re-created every render
 * and close over that render's policy and preview, and the first render happens while the policy
 * is still loading (policy === null).
 */
export const useIncidentPreviewHandlers = ({
  previewScrubIncidents,
  previewAddIncidents,
  setLoadingState,
  setPreview,
  setLoadingPreview,
  handleError
}) => {
  const latestRenderCallbacks = useRef({ setLoadingState, handleError });
  latestRenderCallbacks.current = { setLoadingState, handleError };

  const handleScrubIncidents = useCallback(
    async (policyValue, incidentDates, claimsToScrub) => {
      latestRenderCallbacks.current.setLoadingState();
      const res = await previewScrubIncidents(policyValue, incidentDates, claimsToScrub);
      if (res.ok) {
        setPreview(res.preview);
      } else {
        latestRenderCallbacks.current.handleError(
          { ...res, tryAgain: () => handleScrubIncidents(policyValue, incidentDates, claimsToScrub) },
          { policyValue }
        );
      }
      setLoadingPreview(false);
    },
    [previewScrubIncidents, setPreview, setLoadingPreview]
  );

  const handleAddIncidents = useCallback(
    async (policyValue, incidentsToAdd) => {
      latestRenderCallbacks.current.setLoadingState();
      const res = await previewAddIncidents(policyValue, incidentsToAdd);
      if (res.ok) {
        setPreview(res.preview);
      } else {
        latestRenderCallbacks.current.handleError(
          { ...res, tryAgain: () => handleAddIncidents(policyValue, incidentsToAdd) },
          { policyValue }
        );
      }
      setLoadingPreview(false);
    },
    [previewAddIncidents, setPreview, setLoadingPreview]
  );

  return { handleScrubIncidents, handleAddIncidents };
};
